// frontend/src/features/livestock/photoSubject.ts
// Whose photos are being compared and lined up
// (docs/plans/2026-10-29-livestock-compare-design.md, 8.1): a tank's shots or
// one organism's photos. Pages and components take a subject, never a tank hook.

export type PhotoSubject =
  | { kind: 'tank'; tankId: string }
  | { kind: 'organism'; tankId: string; livestockId: number };

/** The words the shared UI uses for the subject's photos. */
export interface SubjectWords {
  /** In sentences: "Same shot, pick another", "Nearest shot: 3 Mar 2026". */
  noun: string;
  /** Alt text and titles: "Tank shot, 3 Mar 2026". */
  nounTitle: string;
  /** "Add another tank shot to compare". */
  addAnother: string;
  /** "Add tank shots to compare". */
  addSome: string;
  /** Plural for sentences: "Those shots are no longer here". */
  nounPlural: string;
  /** The index failed: "Tank shots didn't load". */
  loadError: string;
  /** Under the reference's side on the compare page. */
  referenceNote: string;
  /** The Line up "make this the reference" step. */
  chooseReferenceTitle: string;
  chooseReferenceBody: string;
  chooseReferenceDetail: string;
  chooseReferenceButton: string;
}

/** Today's tank shot wording, word for word. */
export const TANK_WORDS: SubjectWords = {
  noun: 'shot',
  nounTitle: 'Tank shot',
  addAnother: 'Add another tank shot to compare',
  addSome: 'Add tank shots to compare',
  nounPlural: 'shots',
  loadError: "Tank shots didn't load",
  referenceNote: 'The reference shot: others are lined up to it',
  chooseReferenceTitle: 'Choose a reference shot',
  chooseReferenceBody:
    "Line up works against one reference shot, the tank's cover. Every shot is lined up to match it, so any two shots compare well. Make this shot the reference?",
  chooseReferenceDetail:
    'It becomes the cover on the Tanks page. Line up the other shots against it from here.',
  chooseReferenceButton: 'Use this shot',
};

/**
 * An organism's photos (any livestock type). Its reference is its own, chosen
 * here: it is not a cover anywhere (the Livestock list keeps showing the
 * latest photo; design 12.3).
 */
export const ORGANISM_WORDS: SubjectWords = {
  noun: 'photo',
  nounTitle: 'Photo',
  addAnother: 'Add another photo to compare',
  addSome: 'Add photos to compare',
  nounPlural: 'photos',
  loadError: "Photos didn't load",
  referenceNote: 'The reference photo: others are lined up to it',
  chooseReferenceTitle: 'Choose a reference photo',
  chooseReferenceBody:
    'Line up works against one reference photo of this organism. Every photo is lined up to match it, so any two photos compare well. Make this photo the reference?',
  chooseReferenceDetail:
    "It is only used for lining up: it doesn't change the photo shown on the Livestock page. Line up the other photos against it from here.",
  chooseReferenceButton: 'Use this photo',
};

/** "shot" to "Shot", for the start of a label. */
export function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** A stable key for the subject: the same subject always gives the same key. */
export function subjectKey(subject: PhotoSubject): string {
  return subject.kind === 'tank'
    ? `tank:${subject.tankId}`
    : `organism:${subject.tankId}/${subject.livestockId}`;
}
